<template>
  <v-container class="capture-keys">
    <div class="d-flex justify-space-between align-center mb-4">
      <h1 class="form-title">Activation Keys</h1>
      <v-btn
        v-if="canManage && !unprovisioned && keys.length"
        class="str-btn-primary"
        @click="openCreate"
        data-testid="add-key-btn"
      >+ Add Activation Key</v-btn>
    </div>

    <v-alert v-if="unprovisioned" type="info" variant="tonal" data-testid="unprovisioned-alert">
      No Structure Capture account is linked to this login yet. Contact
      <a href="mailto:partners@structure.io">partners@structure.io</a> to have one set up.
    </v-alert>

    <template v-else>
      <!-- Both halves of the MDM policy, together: the key is useless without the org id
           and this is the only place the customer can read theirs back. -->
      <v-card class="mb-4 pa-4" variant="tonal" data-testid="org-id-card">
        <div class="text-caption text-medium-emphasis">Apple Business Manager Organization ID</div>
        <div class="d-flex align-center">
          <span class="mono text-body-1">{{ organizationId || '—' }}</span>
          <v-btn
            v-if="organizationId"
            size="small"
            variant="text"
            icon
            aria-label="Copy organization ID"
            @click="copyText(organizationId, 'org')"
            data-testid="copy-org-btn"
          >
            <v-icon size="small">{{ copied.org ? 'fas fa-check' : 'fas fa-clipboard' }}</v-icon>
          </v-btn>
        </div>
      </v-card>

      <!-- Empty state doubles as the call to action: a fleet with no key cannot activate. -->
      <v-card v-if="!keys.length && !loading" class="pa-8 text-center" data-testid="first-key-card">
        <v-icon size="48" class="mb-4">fas fa-key</v-icon>
        <h2 class="mb-2">Create your first activation key</h2>
        <p class="mb-6">
          Structure Capture needs one activation key pushed through your MDM before any
          device can activate. You can add more later and rotate between them.
        </p>
        <v-btn v-if="canManage" class="str-btn-primary" @click="openCreate" data-testid="first-key-btn">
          + Add Activation Key
        </v-btn>
      </v-card>

      <template v-else>
        <v-alert
          v-if="unusedKeys.length"
          type="warning"
          variant="tonal"
          class="mb-4"
          data-testid="never-used-alert"
        >
          {{ unusedKeys.length === 1 ? 'One key has' : `${unusedKeys.length} keys have` }}
          never been used to activate a device
          ({{ unusedKeys.map(key => key.label).join(', ') }}). If it has been more than a
          day since you pushed the profile, the MDM app configuration is probably not
          reaching devices — check the key names against the snippet below.
        </v-alert>

        <v-data-table
          :headers="headers"
          :items="keys"
          :loading="loading"
          item-value="id"
          data-testid="keys-table"
        >
          <template #[`item.value`]="{ item }">
            <span class="mono">{{ revealed[item.id] ? item.value : '••••••••••••' }}</span>
            <v-btn
              size="small"
              variant="text"
              icon
              :aria-label="revealed[item.id] ? 'Hide key' : 'Reveal key'"
              @click="revealed[item.id] = !revealed[item.id]"
              data-testid="reveal-btn"
            >
              <v-icon size="small">{{ revealed[item.id] ? 'fas fa-eye-slash' : 'fas fa-eye' }}</v-icon>
            </v-btn>
            <v-btn
              size="small"
              variant="text"
              icon
              aria-label="Copy key"
              @click="copyText(item.value, item.id)"
              data-testid="copy-btn"
            >
              <v-icon size="small">{{ copied[item.id] ? 'fas fa-check' : 'fas fa-clipboard' }}</v-icon>
            </v-btn>
          </template>
          <template #[`item.revoked_on`]="{ item }">
            <v-chip :color="item.revoked_on ? 'grey' : 'green'" size="small">
              {{ item.revoked_on ? 'Revoked' : 'Active' }}
            </v-chip>
          </template>
          <template #[`item.created_on`]="{ item }">{{ formatDate(item.created_on) }}</template>
          <template #[`item.last_used_on`]="{ item }">
            <span v-if="item.last_used_on">{{ formatDate(item.last_used_on) }}</span>
            <v-chip v-else-if="isStale(item)" color="warning" size="x-small" data-testid="never-used-chip">
              Never used
            </v-chip>
            <span v-else>—</span>
          </template>
          <template #[`item.actions`]="{ item }">
            <v-btn
              v-if="canManage && !item.revoked_on"
              size="small"
              variant="text"
              icon
              aria-label="Revoke key"
              @click="askRevoke(item)"
              data-testid="revoke-btn"
            >
              <v-icon>fas fa-ban</v-icon>
            </v-btn>
          </template>
        </v-data-table>

        <!-- The task the customer actually came to do. Assembled from their own values so
             there is nothing left to substitute by hand except the MDM's own tokens. -->
        <v-expansion-panels v-model="openPanel" class="mt-6" data-testid="mdm-panels">
          <v-expansion-panel value="mdm" title="MDM app configuration">
            <v-expansion-panel-text>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="mdmVendor"
                    :items="vendorOptions"
                    label="MDM"
                    variant="outlined"
                    density="compact"
                    hide-details
                    data-testid="vendor-select"
                  ></v-select>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-select
                    v-model="snippetKeyId"
                    :items="activeKeyOptions"
                    label="Activation key"
                    variant="outlined"
                    density="compact"
                    hide-details
                    data-testid="snippet-key-select"
                  ></v-select>
                </v-col>
              </v-row>

              <v-alert v-if="!activeKeyOptions.length" type="warning" variant="tonal" class="mt-4">
                Every key is revoked. Create one to generate a configuration.
              </v-alert>

              <template v-else>
                <!-- Above the snippet, not below: the omission has to be read before the
                     configuration is pasted, not after. -->
                <v-alert
                  v-if="!vendor.serial"
                  type="warning"
                  variant="tonal"
                  class="mt-4"
                  data-testid="generic-warning"
                >
                  This configuration leaves out <code>device_serial</code> and
                  <code>device_name</code>, so your seats will list a model and app version
                  but no serial or name — both in the console and in the CSV export. You
                  are responsible for supplying those two keys: add them with your own
                  MDM's substitution tokens, or with fixed per-device values. Seats are
                  counted per device either way, so billing is unaffected.
                </v-alert>

                <div class="d-flex justify-space-between align-center mt-4 mb-2">
                  <span class="text-caption text-medium-emphasis">
                    <template v-if="vendor.serial">
                      Paste into {{ vendor.label }} as the app's configuration property list.
                    </template>
                    <template v-else>
                      Paste as the app's configuration property list. Valid in any MDM.
                    </template>
                  </span>
                  <v-btn size="small" variant="text" @click="copyText(snippet, 'snippet')" data-testid="copy-snippet-btn">
                    <v-icon size="small" class="mr-1">
                      {{ copied.snippet ? 'fas fa-check' : 'fas fa-clipboard' }}
                    </v-icon>
                    Copy
                  </v-btn>
                </div>
                <pre class="snippet" data-testid="mdm-snippet">{{ snippet }}</pre>
                <p v-if="vendor.serial" class="text-caption mt-2">
                  <code>device_serial</code> and <code>device_name</code> are display only —
                  seats are counted per device regardless, so a missing one costs nothing but
                  legibility. Confirm the two substitution tokens against your own tenant:
                  they vary between MDM versions, and an unsubstituted one shows up as a
                  literal device name. Switch to “Other / no substitution” to leave them out.
                </p>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </template>
    </template>

    <v-dialog v-model="createDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>New Activation Key</v-card-title>
        <v-card-text>
          <v-form ref="createForm" @submit.prevent="submitCreate">
            <v-text-field
              v-model="label"
              label="Label"
              hint="How you will recognise it — a site, a fleet, a rotation date."
              persistent-hint
              :rules="[required]"
              variant="outlined"
              color="primary"
              data-testid="label-input"
            ></v-text-field>
            <v-alert v-if="createError" type="error" variant="tonal" class="mt-3" data-testid="create-error">
              {{ createError }}
            </v-alert>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="saving" @click="createDialog = false">Cancel</v-btn>
          <v-btn class="str-btn-primary" :loading="saving" @click="submitCreate" data-testid="create-submit">
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      :model-value="!!revokeTarget"
      @update:model-value="value => { if (!value) revokeTarget = null; }"
      max-width="520"
      persistent
    >
      <v-card>
        <v-card-title>Revoke “{{ revokeTarget && revokeTarget.label }}”?</v-card-title>
        <v-card-text>
          <p>
            Devices already activated keep working until their licence expires. Any MDM
            profile still carrying this key will stop activating new devices.
          </p>
          <!-- The backend refuses to revoke the last active key without force, because
               doing so lapses the whole fleet. Surface its wording verbatim. -->
          <v-alert v-if="revokeWarning" type="warning" variant="tonal" class="mt-3" data-testid="revoke-warning">
            {{ revokeWarning }}
          </v-alert>
          <v-alert v-if="revokeError" type="error" variant="tonal" class="mt-3" data-testid="revoke-error">
            {{ revokeError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn :disabled="revoking" @click="revokeTarget = null">Cancel</v-btn>
          <v-btn
            color="error"
            variant="flat"
            :loading="revoking"
            @click="doRevoke(!!revokeWarning)"
            data-testid="revoke-confirm"
          >{{ revokeWarning ? 'Revoke anyway' : 'Revoke' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :timeout="2000">{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { reasonFor, useCBERequest } from '@/composables';

const store = useStore();
const { makeCBERequest } = useCBERequest();

// The four keys are frozen by API contract §1 — renaming one breaks every deployed
// customer policy, so they are spelled out here rather than derived.
// Only `organization_id` and `activation_key` are required; the other two are display
// metadata, and an unsubstituted token is passed through verbatim — so a wrong guess here
// shows a literal `{{serialnumber}}` as a device name and never affects licensing.
// `generic` emits just the required pair and therefore works in any MDM.
const MDM_VENDORS = {
  jamf: { label: 'Jamf Pro', serial: '$SERIALNUMBER', name: '$DEVICENAME' },
  intune: { label: 'Microsoft Intune', serial: '{{serialnumber}}', name: '{{devicename}}' },
  workspaceone: {
    label: 'VMware Workspace ONE UEM',
    serial: '{DeviceSerialNumber}',
    name: '{DeviceFriendlyName}',
  },
  iru: { label: 'Iru (previously Kandji)', serial: '$SERIAL_NUMBER', name: '$DEVICE_NAME' },
  mosyle: { label: 'Mosyle', serial: '%SerialNumber%', name: '%DeviceName%' },
  generic: { label: 'Other / no substitution', serial: null, name: null },
};

// A key pushed minutes ago has legitimately not been used yet; a day is long enough that
// silence means the profile is not landing.
const STALE_AFTER_MS = 24 * 60 * 60 * 1000;

const keys = ref([]);
const organizationId = ref('');
const loading = ref(false);
const unprovisioned = ref(false);
const revealed = reactive({});
const copied = reactive({});
const snackbar = ref(false);
const snackbarText = ref('');

const mdmVendor = ref('jamf');
const snippetKeyId = ref(null);

// Open on arrival: assembling this snippet is what the page is for, and a collapsed
// panel reads as an optional extra. A real `v-model` rather than a bound value, or
// clicking it could not close it again.
const openPanel = ref('mdm');

const createDialog = ref(false);
const createForm = ref(null);
const label = ref('');
const createError = ref('');
const saving = ref(false);

const revokeTarget = ref(null);
const revokeWarning = ref('');
const revokeError = ref('');
const revoking = ref(false);

const canManage = computed(() =>
  (store.state.authentication.permissions || []).includes('capture:keys:manage')
);

const headers = [
  { title: 'Label', key: 'label' },
  { title: 'Activation Key', key: 'value', sortable: false },
  { title: 'Created', key: 'created_on' },
  { title: 'Last Used', key: 'last_used_on' },
  { title: 'Status', key: 'revoked_on' },
  { title: '', key: 'actions', sortable: false },
];

const vendorOptions = Object.entries(MDM_VENDORS).map(([value, vendor]) => ({
  title: vendor.label,
  value,
}));

const required = value => !!value || 'Required.';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const activeKeys = computed(() => keys.value.filter(key => !key.revoked_on));

const isStale = (key) =>
  !key.revoked_on &&
  !key.last_used_on &&
  Date.now() - new Date(key.created_on).getTime() > STALE_AFTER_MS;

const unusedKeys = computed(() => activeKeys.value.filter(isStale));

const activeKeyOptions = computed(() =>
  activeKeys.value.map(key => ({ title: key.label, value: key.id }))
);

const vendor = computed(() => MDM_VENDORS[mdmVendor.value]);

const snippet = computed(() => {
  const key = activeKeys.value.find(candidate => candidate.id === snippetKeyId.value);
  if (!key) return '';
  const lines = [
    '<dict>',
    '  <key>organization_id</key>',
    `  <string>${organizationId.value}</string>`,
    '  <key>activation_key</key>',
    `  <string>${key.value}</string>`,
  ];
  // Omitted entirely when the MDM has no token for them: an empty or literal value is
  // worse than an absent key, which the app reads as "not supplied".
  if (vendor.value.serial) {
    lines.push('  <key>device_serial</key>', `  <string>${vendor.value.serial}</string>`);
  }
  if (vendor.value.name) {
    lines.push('  <key>device_name</key>', `  <string>${vendor.value.name}</string>`);
  }
  lines.push('</dict>');
  return lines.join('\n');
});

const fetchKeys = async () => {
  loading.value = true;
  try {
    const body = await makeCBERequest('/piccolo/capture/keys', 'get', {}, {}, { rethrow: true });
    keys.value = body?.keys ?? [];
    organizationId.value = body?.organization_id ?? '';
    unprovisioned.value = false;
  } catch (error) {
    // A role without an account is the normal state between purchase and provisioning,
    // not an error worth a modal.
    if (error.response?.status === 404) {
      unprovisioned.value = true;
    } else {
      store.commit('SET_ERROR_MESSAGE', reasonFor(error));
      store.commit('SET_SHOW_ERROR_MODAL', true);
    }
  } finally {
    loading.value = false;
  }
};

const openCreate = () => {
  label.value = '';
  createError.value = '';
  createDialog.value = true;
};

const submitCreate = async () => {
  createError.value = '';
  const { valid } = await createForm.value.validate();
  if (!valid) return;

  saving.value = true;
  try {
    await makeCBERequest('/piccolo/capture/keys', 'post', { label: label.value }, {}, { rethrow: true });
    createDialog.value = false;
    await fetchKeys();
    notify('Activation key created.');
  } catch (error) {
    createError.value = reasonFor(error);
  } finally {
    saving.value = false;
  }
};

const askRevoke = (key) => {
  revokeTarget.value = key;
  revokeWarning.value = '';
  revokeError.value = '';
};

const doRevoke = async (force) => {
  revoking.value = true;
  revokeError.value = '';
  try {
    await makeCBERequest(
      `/piccolo/capture/keys/${revokeTarget.value.id}`,
      'delete',
      {},
      force ? { force: true } : {},
      { rethrow: true }
    );
    revokeTarget.value = null;
    await fetchKeys();
    notify('Activation key revoked.');
  } catch (error) {
    if (error.response?.status === 409) {
      revokeWarning.value = reasonFor(error);
    } else {
      revokeError.value = reasonFor(error);
    }
  } finally {
    revoking.value = false;
  }
};

const copyText = async (text, slot) => {
  try {
    await navigator.clipboard.writeText(text);
    copied[slot] = true;
    notify('Copied to clipboard.');
    setTimeout(() => { copied[slot] = false; }, 2000);
  } catch {
    notify('Could not copy — select the text and copy it by hand.');
  }
};

function notify(text) {
  snackbarText.value = text;
  snackbar.value = true;
}

// Default the snippet to the newest usable key, and never leave it pointing at one that
// was just revoked.
watch(activeKeys, (available) => {
  if (!available.some(key => key.id === snippetKeyId.value)) {
    snippetKeyId.value = available.length ? available[available.length - 1].id : null;
  }
}, { immediate: true });

onMounted(fetchKeys);
</script>

<style scoped lang="scss">
@import '@/assets/styles/StrVariables.scss'
@import '@/assets/styles/StrButtons.scss'

.mono
  font-family: monospace

.snippet
  font-family: monospace
  font-size: 0.8rem
  padding: 1rem
  border-radius: 4px
  background: rgba(127, 127, 127, 0.12)
  overflow-x: auto
</style>
